import React, { useState, useEffect, useRef } from 'react';
import BotonBuscar from './botones/BotonBuscar.js';
import MensajeError from './MensajeError.js';
import MensajeCargando from './MensajeCargando.js';
import PanelHeaderColapsable from './PanelHeaderColapsable.js';
import styles from './FiltrosGenerico.module.css';

/**
 * Componente genérico para filtros reutilizable en Clubes, Personas y Estadios
 *
 * Props:
 * - filtros: objeto con estado de los filtros
 * - campos: array de configuración de campos [{id, nombre, component, estado, valor, ...}]
 * - onFiltroElegido: callback cuando se elige un filtro (toggle)
 * - onBuscar: callback para ejecutar la búsqueda
 * - isBuscando: boolean indicando si está cargando
 * - errores: array de errores a mostrar
 * - titulo: título de la sección de filtros (default: "Buscar:")
 * - onLimpiar: callback opcional para limpiar filtros
 * - collapsibleEnMobile: boolean para colapsar en mobile (default: true)
 * - initialCollapsed: boolean para determinar si el filtro comienza colapsado (default: false)
 * - hasResults: boolean indicando si hay resultados (para colapsar solo si los hay)
 */
const FiltrosGenerico = (props) => {
  const {
    filtros,
    campos,
    onFiltroElegido,
    onBuscar,
    isBuscando = false,
    errores = [],
    titulo = 'Buscar',
    onLimpiar = null,
    collapsibleEnMobile = true,
    initialCollapsed = false,
    hasResults = false,
  } = props;

  // Detectar si estamos en modo colapsable o no
  const [isColapsable, setIsColapsable] = useState(
    collapsibleEnMobile && typeof window !== 'undefined' && window.innerWidth <= 1024
  );

  // El estado mostrarFiltros solo tiene efecto en modo colapsable
  // En modo no colapsable (desktop), siempre será true
  const [mostrarFiltros, setMostrarFiltros] = useState(isColapsable ? !initialCollapsed : true);

  // Rastrear referencias para detectar cambios
  const wasBuscandoRef = useRef(isBuscando);
  const previousHasResultsRef = useRef(hasResults);

  // Efecto para colapsar/abrir basado en resultados de búsqueda
  useEffect(() => {
    // Solo aplicar lógica de colapso si estamos en modo colapsable (mobile/tablet)
    if (!isColapsable) {
      // En desktop, siempre abierto
      setMostrarFiltros(true);
      wasBuscandoRef.current = isBuscando;
      previousHasResultsRef.current = hasResults;
      return;
    }

    // Modo colapsable (mobile/tablet)
    // Detectar si la búsqueda ACABA DE TERMINAR (cambio de true a false)
    const buscadaAcabaDeterminar = wasBuscandoRef.current && !isBuscando;

    if (buscadaAcabaDeterminar) {
      // La búsqueda terminó, revisar si hay resultados
      if (hasResults) {
        // Hay resultados: cerrar filtros
        setMostrarFiltros(false);
      } else {
        // No hay resultados: abrir filtros para que corrija
        setMostrarFiltros(true);
      }
    }

    wasBuscandoRef.current = isBuscando;
    previousHasResultsRef.current = hasResults;
  }, [isBuscando, hasResults, isColapsable]);

  if (!filtros || !campos) {
    return null;
  }

  // Ejecutar búsqueda (colapso manejado por useEffect)
  const handleBuscar = () => {
    if (onBuscar) {
      onBuscar();
    }
  };

  return (
    <PanelHeaderColapsable
      titulo={titulo}
      className={styles.contenedor}
      colapsableEnMobile={collapsibleEnMobile}
      breakpoint={1024}
      expandido={mostrarFiltros}
      onExpandidoChange={setMostrarFiltros}
      onColapsableChange={setIsColapsable}
      ariaLabelToggle="Expandir/Contraer filtros"
      headerAcciones={
        <div className={styles.botones}>
          {onLimpiar && (
            <button
              className={styles.botonLimpiar}
              onClick={onLimpiar}
              disabled={isBuscando}
              title="Limpiar todos los filtros"
            >
              ✕ Limpiar
            </button>
          )}
          <BotonBuscar
            estilo={styles.botonBuscar}
            onClick={handleBuscar}
            deshabilitado={isBuscando}
          />
        </div>
      }
      alwaysContent={
        <React.Fragment>
          {errores && errores.length > 0 && <MensajeError errores={errores} />}
          {isBuscando && <MensajeCargando />}
        </React.Fragment>
      }
    >
      <React.Fragment>
        <div className={styles.contenedorCampos}>
          {campos.map((campo, index) => (
            <div key={campo.id || index} className={styles.grupoFiltro}>
              {/* Campo selector/toggle */}
              {campo.renderToggle && (
                <div className={styles.toggle}>
                  {campo.renderToggle(campo, filtros, onFiltroElegido)}
                </div>
              )}

              {/* Campo input/select (visible si el toggle está activo) */}
              {campo.activo && campo.renderCampo && (
                <div className={styles.campo}>{campo.renderCampo(campo, filtros)}</div>
              )}
            </div>
          ))}
        </div>

        <div className={styles.pie}>
          {onLimpiar && (
            <button className={styles.botonLimpiarPie} onClick={onLimpiar} disabled={isBuscando}>
              Limpiar filtros
            </button>
          )}
        </div>
      </React.Fragment>
    </PanelHeaderColapsable>
  );
};

export default FiltrosGenerico;
